<template>
  <el-dialog
    title="Import hợp đồng"
    :visible.sync="dialogVisible"
    width="70%"
    :before-close="handleClose"
    class="import-contract-modal"
  >
    <div class="import-content">
      <!-- File Upload Section -->
      <div class="upload-section">
        <div class="upload-header">
          <h4>Chọn file CSV để import</h4>
          <el-button 
            type="text" 
            @click="downloadTemplate"
            icon="el-icon-download"
          >
            Tải file mẫu
          </el-button>
        </div>
        <div class="upload-component">
          <UploadFile 
            ref="uploadFile"
            inputId="contract-upload"
            @uploadFile="handleUploadFile" 
            @deleteFile="handleDeleteFile"
          />
        </div>
      </div>

      <!-- Error Section for Record Limit -->
      <div v-if="previewData.length > 100" class="error-section">
        <el-alert
          title="File vượt quá số lượng bản ghi cho phép"
          type="error"
          :closable="false"
          show-icon
          description="Bạn chỉ được import tối đa 100 bản ghi. Vui lòng chia nhỏ file trước khi import."
        />
      </div>

      <!-- Preview Section -->
      <div v-else-if="previewData.length > 0" class="preview-section">
        <h4>Preview dữ liệu ({{ previewData.length }} dòng):</h4>
        <PreviewTable
          :data="previewData"
          :rules="contractSchema"
          :labels="labels"
          :external-errors="apiErrors"
          @validated="handleValidated"
        />
      </div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Hủy</el-button>
      <el-button 
        type="primary" 
        @click="handleImport"
        :loading="isImporting"
        :disabled="!canImport"
      >
        {{ isImporting ? 'Đang import...' : 'Import' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { IMPORT_CONTRACTS } from "@/core/services/store/contract/contracts.module";
import UploadFile from "@/components/UploadFile";
import PreviewTable from "@/components/PreviewTable";
import { ContractImportInputDTO, ContractImportOutputDTO, ContractValidationErrorDTO, convertPreviewToAPIData } from "@/core/dto/contract.dto";
import { contractSchema } from "@/core/schemas/contract.validation.schema";

export default {
  name: "ImportContractModal",
  components: {
    UploadFile,
    PreviewTable
  },
  props: {
    /**
     * Hiển thị modal import
     * @type {Boolean}
     * @default false
     */
    showModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      previewData: [],
      validationErrors: [],
      isValid: true,
      isImporting: false,
      convertedData: [],
      apiErrors: [],
      contractSchema, // Import schema từ file
      // Mapping tên field/cột để hiển thị tiếng Việt
      labels: {
        rowIndex: 'STT',
        customer_name: 'Họ và tên',
        customer_email: 'Email',
        customer_phone: 'Số điện thoại',
        customer_birthday: 'Ngày sinh',
        customer_address: 'Địa chỉ',
        customer_cccd: 'CCCD',
        center_id: 'Trung tâm',
        branch_id: 'Chi nhánh',
        course_ids: 'Khóa học',
        tuition_payment_id: 'Hình thức đóng học phí',
        tvv_id: 'Mã tư vấn',
        user_intro_id: 'Mã giới thiệu',
        promotion_ids: 'Mã khuyến mại',
        type: 'Loại hợp đồng'
      }
    }
  },
  computed: {
    canImport() {
      return (
        this.convertedData.length > 0 &&
        this.convertedData.length <= 100 &&
        this.isValid
      );
    }
  },
  watch: {
    showModal(val) {
      this.dialogVisible = val;
      if (val) {
        this.resetModalState();
        this.$nextTick(() => {
          if (this.$refs.uploadFile) {
            this.$refs.uploadFile.resetData();
          }
        });
      }
    },
    dialogVisible(val) {
      if (!val) {
        this.$emit('close');
      }
    }
  },
  methods: {
    /**
     * Đóng modal
     */
    handleClose() {
      this.dialogVisible = false;
    },

    /**
     * Reset dữ liệu modal
     */
    resetData() {
      this.resetModalState();
    },

    /**
     * Xử lý khi upload file thành công
     * @param {Array} data - Dữ liệu từ CSV
     */
    async handleUploadFile(data) {
      console.log(data, 'data');
      if (!data || !Array.isArray(data)) {
        console.error('Invalid data received:', data);
        this.$notify({
          title: 'Lỗi',
          message: 'Dữ liệu không hợp lệ',
          type: 'error'
        });
        return;
      }
      
      // Sử dụng DTO để chuẩn hóa dữ liệu
      this.convertedData = ContractImportInputDTO(data);
      this.previewData = this.convertedData;
      
      // Validate dữ liệu
      await this.validateData();
    },

    /**
     * Xử lý khi xóa file
     */
    handleDeleteFile() {
      this.resetModalState();
      // Reset UploadFile component
      this.$nextTick(() => {
        if (this.$refs.uploadFile) {
          this.$refs.uploadFile.resetData();
        }
      });
    },

    

    /**
     * Xử lý kết quả validation từ PreviewTable
     * @param {Object} result - Kết quả validation { errors, errorMap }
     */
        handleValidated({ errors, isValid }) {
          this.validationErrors = errors || [];
          this.isValid = isValid;
        },

    /**
     * Xử lý lỗi từ API
     * @param {Array} errors - Danh sách lỗi từ API
     */
    handleImportErrors(errors) {
      const displayErrors = [];
      errors.forEach(errorData => {
        if (errorData.rowIndex && errorData.errors) {
          // Format có cấu trúc: {rowIndex: 1, errors: {field: "message"}}
          const { rowIndex, errors: fieldErrors } = errorData;
          Object.keys(fieldErrors).forEach(field => {
            displayErrors.push(ContractValidationErrorDTO({
              row: rowIndex,
              field,
              message: fieldErrors[field]
            }));
          });
        } else if (errorData.rowIndex && errorData.message) {
          // Format đơn giản: {rowIndex: 1, message: "error message"}
          displayErrors.push(ContractValidationErrorDTO({
            row: errorData.rowIndex,
            field: 'general',
            message: errorData.message
          }));
        }
      });
      this.validationErrors = displayErrors;
      this.apiErrors = displayErrors;
      const totalRows = new Set(errors.map(e => e.rowIndex)).size;
      this.$notify({
        title: 'Import hoàn thành với lỗi',
        message: `Có ${totalRows} dòng bị lỗi. Vui lòng kiểm tra và sửa lại.`,
        type: 'warning'
      });
    },

    /**
     * Thực hiện import hợp đồng
     */
    async handleImport() {
      if (!this.canImport) {
        return;
      }
      
      this.isImporting = true;
      
      try {
        const payload = convertPreviewToAPIData(this.convertedData);
        const response = await this.$store.dispatch(IMPORT_CONTRACTS, payload);
        
        // Sử dụng DTO để chuẩn hóa response
        const normalizedResponse = ContractImportOutputDTO(response);
        
        if (normalizedResponse.errors && normalizedResponse.errors.length > 0) {
          this.handleImportErrors(normalizedResponse.errors);
        } else {
          this.$emit('import-success');
          this.$notify({
            title: 'Thành công',
            message: `Import thành công ${normalizedResponse.successCount || this.convertedData.length} bản ghi`,
            type: 'success'
          });
        }
        
      } catch (error) {
        const resp = error && error.response ? error.response : null;
        const data = resp ? resp.data : (error && error.data ? error.data : null);
        
        // Ưu tiên lỗi root-level errors (cấu trúc mới)
        if (data && Array.isArray(data.errors)) {
          this.handleImportErrors(data.errors);
        } else if (data && data.data && Array.isArray(data.data.errors)) {
          // Fallback theo cấu trúc cũ
          this.handleImportErrors(data.data.errors);
        } else {
          this.$notify({
            title: 'Lỗi',
            message: data?.message || 'Có lỗi xảy ra khi import',
            type: 'error'
          });
        }
      } finally {
        this.isImporting = false;
      }
    },

    /**
     * Lấy label của field để hiển thị
     * @param {String} field - Tên field
     * @returns {String}
     */

    /**
     * Reset trạng thái modal
     */
        resetModalState() {
          this.isImporting = false;
          this.previewData = [];
          this.validationErrors = [];
          this.apiErrors = [];
          this.isValid = true;
          this.convertedData = [];
        },

    /**
     * Tải file mẫu
     */
    downloadTemplate() {
      const link = document.createElement('a');
      link.href = '/excel/File mẫu import hợp đồng.csv';
      link.download = 'File mẫu import hợp đồng.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
</script>

<style scoped>
.import-contract-modal .el-dialog__body {
  padding: 20px;
}

.import-content {
  max-height: none;
  overflow: visible;
}

.upload-section {
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.upload-header h4 {
  margin: 0;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
}

.upload-component {
  margin-top: 12px;
}

.preview-section {
  margin-bottom: 24px;
}

.preview-section h4 {
  margin: 0 0 16px 0;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
}

/* Table scroll styling */
.preview-section .el-table {
  max-height: 400px;
  overflow-y: auto;
}

.preview-section .el-table__body-wrapper {
  max-height: 350px;
  overflow-y: auto;
}


.dialog-footer {
  text-align: right;
}
</style>